import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle, 
  Award, 
  Users, 
  TrendingUp, 
  FileCheck, 
  Search, 
  Clock,
  Globe,
  Briefcase,
  ShoppingCart,
  Zap,
  Lock,
  Star,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import ContactPopup from '../components/ContactPopup';
import PricingSection from '../components/PricingSection';
import Footer from '../components/Footer';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-700 flex-shrink-0" />
              <span className="text-xl sm:text-2xl font-bold text-gray-900">trademark website name</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">Services</button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-700 font-medium transition-colors">Contact</button>
              <Button 
                onClick={() => setIsPopupOpen(true)}
                className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-3">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded">Services</button>
              <button onClick={() => scrollToSection('pricing')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded">Contact</button>
              <Button 
                onClick={() => setIsPopupOpen(true)}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold"
              >
                Get Started
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-amber-50"></div>
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGxhd3xlbnwwfHx8fDE3NzU4NjA4MzN8MA&ixlib=rb-4.1.0&q=85" 
            alt="Corporate Business District"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-4 py-2 text-sm font-semibold">
                <Award className="inline mr-2 h-4 w-4" />
                USPTO Filing Experts
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Protect Your Brand with
                <span className="text-blue-700"> Expert Trademark Registration</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                Secure your intellectual property rights with our comprehensive trademark registration services. From initial search to federal filing, we handle every step of the USPTO process with precision and expertise.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setIsPopupOpen(true)}
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold px-8 py-7 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Shield className="mr-2 h-5 w-5" />
                  Register My Trademark
                </Button>
                <Button 
                  onClick={() => scrollToSection('pricing')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 font-bold px-8 py-7 text-lg rounded-lg transition-all duration-300"
                >
                  View Pricing Plans
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-8 pt-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <span className="text-gray-700 font-medium">1000+ Trademarks Filed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="h-6 w-6 text-amber-500" />
                  <span className="text-gray-700 font-medium">98% Success Rate</span>
                </div>
              </div>
            </div>
            
            <div className="relative hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1775046318523-b5227f41fe97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxsZWdhbCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzU4NjA4MTZ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Professional Legal Expert"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">About Us</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Your Trusted Trademark Registration Partner</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-6">
              With over 15 years of dedicated experience in intellectual property law, trademark website name has established itself as a leading authority in trademark registration services across the United States. We specialize exclusively in helping businesses, entrepreneurs, and brands protect their most valuable asset—their identity.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our mission is simple: to make trademark registration accessible, affordable, and stress-free for businesses of all sizes. From startups launching their first product to established corporations expanding their brand portfolio, we provide the expertise and personalized attention that ensures your trademark journey is smooth and successful. We understand that your brand represents years of hard work, innovation, and reputation—and we're committed to protecting it with the same dedication you've invested in building it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Award className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Expert Team</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our experienced trademark attorneys and legal professionals have successfully filed thousands of trademark applications with the USPTO, ensuring your brand receives the best protection possible.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Each member of our team brings specialized knowledge in trademark law, intellectual property strategy, and USPTO procedures. We stay current with the latest regulatory changes and examination guidelines to provide you with cutting-edge service. Our attorneys are licensed to practice before the USPTO and have extensive experience handling complex trademark matters, office actions, and oppositions.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Shield className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Comprehensive Protection</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We provide end-to-end trademark registration services, from conducting thorough searches to handling office actions, ensuring complete brand protection throughout the entire USPTO filing process.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our comprehensive approach includes detailed trademark availability searches, strategic class selection, specimen preparation, and proactive monitoring. We don't just file applications—we build trademark strategies that align with your business goals and growth plans. From initial consultation through registration and renewal, we're your dedicated partner in brand protection.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <TrendingUp className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Proven Success Rate</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  With a 98% approval rate and meticulous attention to detail, we maximize your chances of successful trademark registration while minimizing delays and office action responses.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our exceptional success rate is built on thorough preparation, strategic filing practices, and deep understanding of USPTO examination standards. We anticipate potential issues before they arise and craft applications that meet or exceed all federal requirements. When challenges do occur, our experienced team responds with persuasive legal arguments backed by case law and precedent.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Businesses Choose trademark website name</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In a crowded market of trademark service providers, trademark website name stands out through our unwavering commitment to client success, transparent communication, and personalized service. We're not a faceless corporation or automated filing service—we're a team of dedicated professionals who understand that behind every trademark application is a business owner's dream, hard work, and livelihood.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Our clients appreciate our straightforward approach, honest assessments, and commitment to education. We explain complex legal concepts in plain language, keep you informed at every stage, and make ourselves available to answer your questions. When you work with trademark website name, you're not just getting a service—you're gaining a trusted advisor invested in your brand's long-term success.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="text-3xl font-bold text-blue-700 mb-2">1000+</div>
                  <div className="text-sm text-gray-600">Successful Registrations</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="text-3xl font-bold text-blue-700 mb-2">15+</div>
                  <div className="text-sm text-gray-600">Years of Experience</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="text-3xl font-bold text-blue-700 mb-2">98%</div>
                  <div className="text-sm text-gray-600">Approval Rate</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="text-3xl font-bold text-blue-700 mb-2">50+</div>
                  <div className="text-sm text-gray-600">Industries Served</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trademark Overview Section */}
      <section id="overview" className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Overview</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">What is Trademark Registration?</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                A trademark is a unique symbol, word, phrase, design, or combination that identifies and distinguishes your products or services from others in the marketplace. Federal trademark registration with the USPTO provides nationwide legal protection for your brand.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Registering your trademark gives you exclusive rights to use your brand mark in connection with your goods or services, prevents others from using confusingly similar marks, and provides a legal foundation for brand enforcement and expansion.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 rounded-full p-2 mt-1">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Exclusive Rights</h4>
                    <p className="text-gray-600">Nationwide protection and exclusive use of your trademark</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 rounded-full p-2 mt-1">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Legal Presumption</h4>
                    <p className="text-gray-600">Legal presumption of ownership and validity in federal courts</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 rounded-full p-2 mt-1">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Brand Value</h4>
                    <p className="text-gray-600">Increases business value and provides licensing opportunities</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1625419693247-fe1712a18a46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHx0cmFkZW1hcmt8ZW58MHx8fHwxNzc1ODYwODU4fDA&ixlib=rb-4.1.0&q=85" 
                alt="Trademark Symbols"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Our Services</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Comprehensive Trademark Registration Services</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-4">
              We offer complete trademark registration solutions tailored to protect your brand at every stage of the process. From initial consultation to final registration and beyond, our full-service approach ensures your intellectual property receives the professional attention it deserves.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our trademark services are designed to remove the complexity and uncertainty from the registration process. Whether you're protecting your first trademark or managing an extensive brand portfolio, we provide the expertise, technology, and personalized support to navigate the USPTO process efficiently and effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Search className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Comprehensive Trademark Search</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our thorough trademark search service goes beyond basic database queries. We conduct comprehensive searches of federal and state trademark databases, common law sources, domain names, and business registries to identify potential conflicts before you invest in your application.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our detailed search reports include likelihood of confusion analysis, registration probability assessment, and strategic recommendations. We help you understand the trademark landscape in your industry and identify opportunities to strengthen your mark. This proactive approach saves time, money, and prevents costly rebranding down the line.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <FileCheck className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Expert Application Preparation & Filing</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our experienced team prepares and electronically files your trademark application with meticulous attention to detail. We ensure accurate classification of your goods and services, compelling descriptions that meet USPTO standards, and proper specimen submission that demonstrates actual use in commerce.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We craft applications that anticipate examiner questions and minimize the likelihood of office actions. From selecting the appropriate filing basis to determining the correct international classes, our strategic approach maximizes your chances of smooth approval while protecting the full scope of your brand's commercial activities.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">USPTO Communication Management</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We serve as your dedicated liaison with the United States Patent and Trademark Office throughout the entire examination process. Our team monitors your application status, responds to all USPTO correspondence promptly, and ensures compliance with every procedural requirement and deadline.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  From publication for opposition to final registration, we handle all communications with professionalism and precision. You'll receive regular updates on your application's progress, plain-language explanations of any USPTO actions, and strategic guidance for navigating any challenges that arise during the examination process.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Clock className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Strategic Office Action Response</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  When the USPTO issues an office action raising concerns about your application, our experienced attorneys craft strategic legal responses that address examiner objections with persuasive arguments, supporting evidence, and applicable case law. We don't just answer questions—we advocate for your trademark rights.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our office action response service includes comprehensive analysis of the examiner's concerns, research into relevant precedents, and creative solutions to overcome refusals. Whether facing likelihood of confusion issues, descriptiveness objections, or procedural matters, we leverage our deep USPTO experience to find pathways to registration.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Proactive Trademark Monitoring</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Trademark registration is just the beginning of brand protection. Our monitoring service keeps watch over new trademark applications and registrations that could conflict with your mark. We identify potential infringements early, when enforcement is most effective and cost-efficient.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our comprehensive monitoring includes USPTO watch services, marketplace surveillance, and domain name monitoring. You'll receive regular reports on potentially conflicting marks with our analysis and recommendations. This proactive approach protects your trademark investment and prevents brand dilution before it impacts your business.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Lock className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Comprehensive Brand Protection Strategy</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Beyond trademark registration, we provide strategic counseling on building and maintaining a strong trademark portfolio. Our brand protection services include enforcement strategy development, licensing guidance, trademark portfolio management, and expansion planning for growing businesses.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We help you develop comprehensive policies for trademark use, create brand guidelines, establish policing procedures, and plan for international protection. Whether you're launching new products, entering new markets, or defending against infringement, our strategic approach ensures your brand remains protected and valuable.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-16 bg-gradient-to-r from-blue-50 to-amber-50 rounded-2xl p-8 md:p-12">
            <div className="text-center max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">End-to-End Service Excellence</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                What truly sets our trademark services apart is our commitment to being your partner throughout the entire brand protection journey. We don't just file applications and disappear—we build lasting relationships with our clients, providing ongoing support, education, and guidance as your business grows and your trademark needs evolve.
              </p>
              <p className="text-gray-700 leading-relaxed">
                From your initial consultation through registration, renewal, and beyond, you'll have access to experienced professionals who understand your business, respond promptly to your questions, and provide strategic advice tailored to your specific situation. This comprehensive, relationship-based approach is why so many of our clients return to us for all their trademark needs and refer their colleagues and partners to our services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Why Choose Us</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">What Sets Us Apart</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-4">
              In the competitive landscape of trademark services, choosing the right partner can make the difference between a smooth registration process and a frustrating, costly experience. At trademark website name, we've built our reputation on delivering exceptional results through specialized expertise, personalized service, and unwavering commitment to client success.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We understand that your trademark represents more than just a legal registration—it's the culmination of your vision, hard work, and brand identity. That's why we approach every case with the attention, expertise, and dedication it deserves, treating your success as our own.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 rounded-lg p-3 mt-1 flex-shrink-0">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Specialized Expertise in Trademark Law</h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      Unlike general practice law firms that handle trademarks as one of many services, we focus exclusively on trademark registration and brand protection. This specialized focus means our team stays at the forefront of trademark law developments, USPTO policy changes, and examination trends.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      Our attorneys have conducted thousands of trademark searches, filed countless applications across all industries, and successfully navigated complex office actions and oppositions. This depth of experience translates directly into better outcomes for your trademark application—higher approval rates, fewer delays, and strategic guidance you can trust.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 rounded-lg p-3 mt-1 flex-shrink-0">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Personal Case Manager</h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      From your first consultation through final registration, you'll work with a dedicated case manager who knows your business, understands your brand goals, and provides personalized guidance tailored to your specific situation. No automated systems, no faceless service—just genuine human expertise and attention.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      Your case manager serves as your direct point of contact, providing regular status updates, answering questions promptly, and proactively addressing any issues that arise. This personalized approach ensures you're never left wondering about your application's progress or what happens next.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 rounded-lg p-3 mt-1 flex-shrink-0">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Exceptional Speed Without Compromise</h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      In trademark registration, timing matters. Delays can mean losing priority to competitors or missing crucial business deadlines. We prioritize efficiency at every stage, with most applications prepared and filed within 24-48 hours of receiving complete information—without ever sacrificing the quality and attention to detail that ensure success.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      Our streamlined processes, experienced team, and commitment to responsiveness mean your trademark application moves forward quickly and correctly. When office actions arise, we respond promptly with well-researched, persuasive arguments that keep your application on track for timely approval.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 rounded-lg p-3 mt-1 flex-shrink-0">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent, Honest Pricing</h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      We believe in straightforward, transparent pricing with no hidden fees or surprise charges. Our flat-rate package pricing is clearly outlined upfront, so you know exactly what you're paying for and what services are included. No billing surprises, no unexpected invoices—just honest, competitive pricing.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      Whether you're a bootstrapped startup or an established corporation, we offer packages designed to fit different needs and budgets. Every package includes the core services necessary for successful registration, with clear options for additional services when needed. Our pricing reflects our commitment to making professional trademark services accessible to businesses of all sizes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-blue-100">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The trademark website name Difference</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                What truly distinguishes trademark website name is our holistic approach to client service. We don't view trademark registration as a transactional process—it's a partnership. From the moment you contact us, you'll experience the difference that comes from working with professionals who genuinely care about your success and are invested in protecting what you've built.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our clients consistently praise our responsiveness, clear communication, and ability to explain complex legal concepts in understandable terms. We take the mystery out of trademark law, empowering you with knowledge while handling the technical complexities on your behalf. This educational approach means you make informed decisions about your brand protection strategy, understand the value of your trademark investment, and feel confident throughout the entire process.
              </p>
              <p className="text-gray-700 leading-relaxed">
                But perhaps most importantly, we're committed to building long-term relationships. Many of our clients return to us for trademark renewals, portfolio expansions, and strategic guidance as their businesses grow. These ongoing partnerships are a testament to the quality of our service and the trust we build with every client. When you choose trademark website name, you're not just hiring a service provider—you're gaining a dedicated partner committed to your brand's long-term protection and success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section - Continue in next part */}
      <section id="process" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Our Process</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Simple 3-Step Registration Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've streamlined the trademark registration process to make it straightforward and stress-free.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-8">
                  <div className="absolute -top-6 left-8 bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                    1
                  </div>
                  <div className="mt-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Consultation & Search</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      We begin with a comprehensive consultation to understand your brand and business goals. Our team conducts a thorough trademark search to identify any potential conflicts and assess the registrability of your desired mark.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Initial brand consultation</span>
                      </li>
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Federal database search</span>
                      </li>
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Conflict analysis report</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="relative">
              <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-8">
                  <div className="absolute -top-6 left-8 bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                    2
                  </div>
                  <div className="mt-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Application Preparation</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Our legal experts prepare your trademark application with meticulous attention to detail, ensuring all classifications, descriptions, and specimens meet USPTO requirements for the best chance of approval.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Application drafting</span>
                      </li>
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Classification selection</span>
                      </li>
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>USPTO e-filing</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="relative">
              <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-8">
                  <div className="absolute -top-6 left-8 bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                    3
                  </div>
                  <div className="mt-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Monitoring & Registration</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      We monitor your application throughout the USPTO examination process, respond to any office actions, and guide your trademark to successful registration, providing ongoing protection for your brand.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Application tracking</span>
                      </li>
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Office action responses</span>
                      </li>
                      <li className="flex items-center text-gray-600">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                        <span>Registration certificate</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Import Pricing Section */}
      <PricingSection />

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Industries We Serve</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Trademark Protection Across All Industries</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-4">
              Every industry faces unique branding challenges and trademark considerations. At trademark website name, we provide specialized trademark registration services tailored to the specific needs and regulatory landscapes of diverse business sectors across the United States.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our team's extensive experience spans countless industries, from cutting-edge technology startups to traditional manufacturing, creative services to healthcare. We understand the nuances of trademark protection in your field and apply industry-specific insights to strengthen your application and protect what makes your brand unique in your market.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">E-Commerce & Retail</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Online marketplaces, direct-to-consumer brands, retail stores, and consumer product companies requiring strong brand protection in competitive markets</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Technology & Software</h3>
                <p className="text-gray-600 text-sm leading-relaxed">SaaS platforms, mobile applications, software development, AI/ML companies, and tech startups navigating complex trademark landscapes</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Professional Services</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Consulting firms, legal practices, accounting services, marketing agencies, and business advisory services building reputation-based brands</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Food & Beverage</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Restaurants, food manufacturers, beverage brands, catering services, and hospitality businesses requiring distinctive brand identities</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Fashion & Apparel</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Clothing brands, accessory designers, footwear companies, luxury goods, and lifestyle products in highly competitive creative markets</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Health & Wellness</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Medical practices, fitness brands, beauty products, wellness services, and healthcare companies navigating regulated industry requirements</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Entertainment & Media</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Content creators, production companies, publishers, streaming services, and media organizations protecting creative brand assets</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="h-7 w-7 text-blue-700" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Manufacturing & Industrial</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Industrial manufacturers, equipment producers, automotive suppliers, and consumer goods companies protecting product brands and innovations</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-amber-50 rounded-2xl p-8 md:p-12">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Industry-Specific Expertise You Can Trust</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                What sets our service apart is our deep understanding of how trademark protection intersects with your industry's unique challenges. We don't take a one-size-fits-all approach—instead, we tailor our strategies to address the specific trademark issues, competitive dynamics, and regulatory considerations that impact your sector.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Whether you're a tech startup navigating a crowded app marketplace, a fashion brand building recognition in a trend-driven industry, or a professional services firm establishing thought leadership, we bring relevant experience and industry insights that strengthen your trademark application and long-term brand strategy.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our clients span from bootstrapped startups to Fortune 500 companies, solo practitioners to multinational corporations. Regardless of your industry or business size, you'll receive the same level of expertise, attention, and commitment to protecting your brand's unique identity in your market. We've successfully registered trademarks across virtually every business category, and we're ready to apply that experience to your trademark registration needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Key Benefits</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Trademark Registration Matters</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-4">
              Federal trademark registration is one of the most important investments you can make in your business. It transforms your brand from a simple business name into a protected legal asset with nationwide recognition and enforcement power.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Beyond the legal protections, trademark registration provides tangible business benefits that impact your company's value, growth potential, and competitive position. Understanding these benefits helps you appreciate why trademark registration is essential for any serious business building long-term brand equity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                  <Lock className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Exclusive Nationwide Legal Rights</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Federal trademark registration grants you exclusive nationwide rights to use your mark in connection with your specific goods or services. This means no one else can legally use a confusingly similar mark in your industry across the entire United States, regardless of whether you're actively doing business in every state.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  These exclusive rights prevent brand dilution, protect your market position, and give you legal grounds to stop competitors from trading on your brand reputation or confusing your customers. Without registration, your trademark rights are limited to the specific geographic areas where you actually use the mark, leaving gaps for competitors to exploit.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Presumption of Ownership & Validity</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  When you register your trademark federally, you gain a legal presumption that you own the mark and have the exclusive right to use it. This shifts the burden of proof in legal disputes—infringers must prove your trademark is invalid rather than you having to prove your rights exist.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  This presumption dramatically strengthens your position in enforcement actions, negotiations, and litigation. It makes cease and desist letters more effective, settlement negotiations more favorable, and court cases more likely to succeed. The registration certificate itself serves as prima facie evidence of your trademark rights in federal court.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                  <TrendingUp className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Increased Business Valuation & Assets</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  A federally registered trademark is a quantifiable intangible asset that appears on your balance sheet and significantly increases your company's overall value. Investors, acquirers, and lenders view registered trademarks as valuable assets that contribute to enterprise value during fundraising, mergers, acquisitions, or lending evaluations.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Registered trademarks also create revenue opportunities through licensing agreements, franchising, and brand partnerships. You can monetize your brand by licensing it to others, expanding into new product categories, or entering into co-branding arrangements—all while maintaining ownership and control of your core trademark rights.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                  <Globe className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Powerful Online & Digital Protection</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  In today's digital marketplace, trademark registration provides critical tools for protecting your brand online. Registered trademarks give you access to streamlined takedown procedures on major e-commerce platforms like Amazon, eBay, and Etsy, allowing you to quickly remove counterfeit products and unauthorized sellers exploiting your brand.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Registration also helps you combat domain name cybersquatting through the Uniform Domain Name Dispute Resolution Policy (UDRP), recover infringing domain names, prevent social media impersonation, and enforce your rights against digital infringers. These online protections are increasingly essential as more commerce moves to digital channels.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                  <CheckCircle className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Market Credibility & Competitive Edge</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  The ® symbol that comes with federal registration signals to customers, partners, and competitors that your brand is legitimate, professionally established, and legally protected. This credibility is particularly valuable for new businesses, startups, and companies entering competitive markets where brand trust drives purchasing decisions.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Registered trademarks also deter potential infringers from even attempting to use similar marks. Many businesses conduct trademark searches before launching new brands—when they discover your registered trademark, they'll typically choose different branding rather than risk legal conflict. This preventive effect protects your brand without requiring enforcement action.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                  <Award className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Foundation for Global Brand Protection</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  U.S. federal trademark registration serves as the foundation for international trademark protection. It establishes your priority date and ownership claims, which can be leveraged when seeking registration in foreign countries through international treaties like the Madrid Protocol.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  As your business expands internationally, your U.S. registration provides a springboard for protecting your brand in overseas markets. Many countries offer streamlined filing procedures for marks already registered in the United States, and your U.S. registration date can establish priority over later foreign applicants trying to register your mark abroad.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-blue-100">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The True Value of Trademark Registration</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                While the legal protections of trademark registration are significant, the real value extends far beyond courtroom victories. Trademark registration is fundamentally about protecting the reputation, goodwill, and customer relationships you've built through years of hard work. Your trademark represents the promise you make to customers, the quality they expect, and the trust they place in your brand.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Every dollar invested in building brand awareness, every positive customer experience, every marketing campaign, and every product improvement contributes to your trademark's value. Without federal registration, you risk losing all that investment to competitors who copy your brand, confuse your customers, or dilute your market position. Registration protects not just a name or logo, but the entire business value tied to that brand identity.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Consider trademark registration as insurance for your brand—you hope you never need to enforce your rights, but having federal registration ensures you're protected if challenges arise. The peace of mind, business opportunities, and defensive protection that registration provides far outweigh the modest investment required. In an increasingly competitive business environment, federal trademark registration isn't just advisable—it's essential for any business serious about long-term success and brand protection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals / Achievements */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Our Achievements</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Trusted by Businesses Nationwide</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our track record speaks for itself. Join thousands of satisfied clients who have successfully protected their brands with our help.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-blue-700 mb-3">1000+</div>
                <p className="text-gray-600 font-medium">Trademarks Filed</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-blue-700 mb-3">98%</div>
                <p className="text-gray-600 font-medium">Success Rate</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-blue-700 mb-3">15+</div>
                <p className="text-gray-600 font-medium">Years Experience</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 text-center">
              <CardContent className="p-8">
                <div className="text-5xl font-bold text-blue-700 mb-3">24/7</div>
                <p className="text-gray-600 font-medium">Client Support</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-16 bg-gradient-to-r from-blue-50 to-amber-50 rounded-2xl p-12">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">USPTO Filing Experts</h3>
                <p className="text-gray-600">Certified professionals with extensive USPTO experience</p>
              </div>
              <div>
                <Star className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">5-Star Rated Service</h3>
                <p className="text-gray-600">Consistently top-rated by our satisfied clients</p>
              </div>
              <div>
                <Award className="h-12 w-12 text-blue-700 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Industry Recognition</h3>
                <p className="text-gray-600">Award-winning trademark registration services</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">FAQ</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about trademark registration.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                What is a trademark and why do I need one?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                A trademark is a unique identifier (word, phrase, symbol, or design) that distinguishes your goods or services from others in the marketplace. Federal trademark registration provides nationwide legal protection, exclusive rights to use your mark, and the ability to prevent others from using confusingly similar marks. It's essential for protecting your brand identity, building customer trust, and increasing your business value.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                How long does the trademark registration process take?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                The USPTO typically takes 8-12 months to process a trademark application from filing to registration, though timelines can vary based on office actions and potential oppositions. We file your application within 24-48 hours of receiving complete information, and we monitor and manage your application throughout the entire process to ensure the fastest possible approval.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                What's the difference between ™ and ®?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                The ™ symbol can be used by anyone claiming trademark rights in a mark, even without federal registration. The ® symbol can only be used after your trademark is officially registered with the USPTO. Federal registration provides significantly stronger legal protections, nationwide rights, and the legal presumption of ownership that makes enforcement much easier.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                Do I need to use my trademark before filing?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                You can file a trademark application based on either current use in commerce or "intent to use." If you're already using your mark in business, you can file based on actual use. If you plan to use it soon, you can file an intent-to-use application, which reserves your rights while you prepare for launch. However, you must demonstrate actual use before the trademark will be registered.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                How much does trademark registration cost?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                Our service fees range from $45 to $349 depending on the package you choose, plus USPTO government filing fees (currently $250-$350 per class of goods/services). Our transparent pricing includes all professional services outlined in your selected package, with no hidden fees. The total investment protects your brand for 10 years with renewal options.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                What happens if my trademark is refused?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                If the USPTO issues an office action (refusal or objection), we'll work with you to craft a strategic response addressing the examiner's concerns. Our experienced team has successfully overcome numerous office actions through persuasive legal arguments and evidence. While we cannot guarantee approval, our 98% success rate demonstrates our expertise in navigating the registration process.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                Can I trademark a business name?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                Yes, but trademark law protects brand names used in commerce to identify goods or services, not just company names. Your business name can be trademarked if you use it to identify your products or services. However, registering a business entity with your state doesn't provide trademark protection—you need federal trademark registration for nationwide rights.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors">
              <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-blue-700 py-5">
                How do I maintain my trademark registration?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                After registration, you must file maintenance documents with the USPTO: a Declaration of Use between years 5-6, and renewal applications every 10 years. You must also continue using your trademark in commerce and monitor for potential infringements. Our trademark monitoring services help you stay on top of these requirements and protect your brand long-term.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Strong CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-700 to-blue-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.pexels.com/photos/6814524/pexels-photo-6814524.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
            alt="Professional Consulting"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white border-white/30 mb-6 px-6 py-2">
            Limited Time Offer
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Protect Your Brand Today
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Don't risk losing your brand identity to competitors. Start your trademark registration now and secure your business's future with our expert legal team.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => setIsPopupOpen(true)}
              size="lg"
              className="bg-white text-blue-700 hover:bg-gray-100 font-bold px-10 py-7 text-lg rounded-lg shadow-2xl hover:shadow-xl transition-all duration-300"
            >
              <Shield className="mr-2 h-6 w-6" />
              Register My Trademark
            </Button>
            <Button 
              onClick={() => scrollToSection('contact')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-bold px-10 py-7 text-lg rounded-lg transition-all duration-300"
            >
              Contact Us Now
            </Button>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 text-white">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-6 w-6" />
              <span className="font-medium">Fast 24-48 Hour Filing</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-6 w-6" />
              <span className="font-medium">98% Success Rate</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-6 w-6" />
              <span className="font-medium">Expert Legal Team</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-800 border-blue-200 mb-4">Contact Us</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Get in Touch with Our Experts</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Have questions about trademark registration? Our team is here to help you protect your brand.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <Card className="border-2 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 p-4 rounded-lg">
                      <MapPin className="h-8 w-8 text-blue-700" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Our Office</h3>
                      <p className="text-gray-600 leading-relaxed">
                        500 N Brand Blvd #2000<br />
                        Glendale, CA 91203<br />
                        United States
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 p-4 rounded-lg">
                      <Phone className="h-8 w-8 text-blue-700" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Phone</h3>
                      <a href="tel:+11231234567" className="text-blue-700 hover:text-blue-800 font-medium text-lg">
                        +1 (123) 123-4567
                      </a>
                      <p className="text-gray-600 mt-1">Monday - Friday, 9AM - 6PM PST</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 p-4 rounded-lg">
                      <Mail className="h-8 w-8 text-blue-700" />
                    </div>
                    <div className="w-full overflow-hidden">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
                      <a href="mailto:info@trademarkwebsitename.com" className="text-blue-700 hover:text-blue-800 font-medium text-base sm:text-lg break-all block">
                        info@trademarkwebsitename.com
                      </a>
                      <p className="text-gray-600 mt-1">We respond within 24 hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-gradient-to-br from-blue-50 to-amber-50 p-6 sm:p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Business Hours</h3>
                <div className="space-y-2 text-sm sm:text-base text-gray-700">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Monday - Friday:</span>
                    <span className="text-right">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Saturday:</span>
                    <span className="text-right">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">Sunday:</span>
                    <span className="text-right">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            <Card className="border-2 border-blue-200 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Contact Form</h3>
                <p className="text-gray-600 mb-6">Fill out the form below or click the button to open our detailed registration form.</p>
                
                <Button 
                  onClick={() => setIsPopupOpen(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 mb-6"
                >
                  <Shield className="mr-2 h-6 w-6" />
                  Start Your Trademark Application
                </Button>

                <div className="bg-blue-50 p-6 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-3">Why Choose Our Services?</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                      <span>Expert trademark attorneys</span>
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                      <span>98% approval success rate</span>
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                      <span>Fast 24-48 hour filing</span>
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                      <span>Transparent pricing, no hidden fees</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default Home;
